<script setup>
import { computed, onMounted, ref } from "vue"
import FullCalendar from "@fullcalendar/vue3"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import esLocale from "@fullcalendar/core/locales/es"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"
import EditarCitaModal from "./components/EditarCitaModal.vue"
import HistorialCitaModal from "./components/HistorialCitaModal.vue"

const { user, hasRole } = useAuth()
const citaEnEdicion = ref(null)
const citaEnHistorial = ref(null)

// admin/rrhh/supervisor ven el calendario completo y pueden filtrar por
// cualquier asesor; el resto solo puede alternar entre "todas" y "propias".
const esPrivilegiado = computed(() => hasRole("admin") || hasRole("rrhh") || hasRole("supervisor"))
const asesoresLideres = ref([])
const filtroAsesor = ref("")

async function cargarAsesoresLideres() {
  if (!esPrivilegiado.value) return
  try {
    const { data } = await api.get("/citas/asesores-lideres")
    asesoresLideres.value = data.asesoresLideres
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el listado de asesores",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

onMounted(cargarAsesoresLideres)

function alCambiarFiltro() {
  recargarRef.value?.getApi().refetchEvents()
}

function cerrarEdicion() {
  citaEnEdicion.value = null
}

function cerrarHistorial() {
  citaEnHistorial.value = null
}

const COLORES_ESTADO = {
  pendiente: "#fb6340",
  confirmada: "#11cdef",
  completada: "#2dce89",
  cancelada: "#8898aa"
}

const LABELS_ESTADO = {
  pendiente: "Pendiente",
  confirmada: "Confirmada",
  completada: "Completada",
  cancelada: "Cancelada"
}

function formatFecha(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

function asesorLiderIdFiltro() {
  if (esPrivilegiado.value) return filtroAsesor.value || undefined
  return filtroAsesor.value === "propias" ? user.value?.id : undefined
}

async function cargarEventos(info, successCallback, failureCallback) {
  try {
    const desde = info.startStr.slice(0, 10)
    const hasta = info.endStr.slice(0, 10)

    const { data } = await api.get("/citas", { params: { desde, hasta, asesorLiderId: asesorLiderIdFiltro(), archivada: "false" } })

    const eventos = data.citas.map((c) => ({
      id: String(c.id),
      title: `${c.nombreCliente} — ${c.asesorLiderNombre}`,
      start: `${c.fecha.slice(0, 10)}T${c.horaInicio}`,
      end: `${c.fecha.slice(0, 10)}T${c.horaFin}`,
      color: COLORES_ESTADO[c.estado] || "#8898aa",
      extendedProps: c
    }))

    successCallback(eventos)
  } catch (err) {
    failureCallback(err)
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el calendario de citas",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

async function cambiarEstado(cita, estado) {
  try {
    await api.put(`/citas/${cita.id}/estado`, { estado })
    Swal.fire({ icon: "success", title: "Cita actualizada", timer: 1500, showConfirmButton: false })
    recargarRef.value?.getApi().refetchEvents()
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar la cita",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

const recargarRef = ref(null)

async function alGuardarEdicion() {
  cerrarEdicion()
  recargarRef.value?.getApi().refetchEvents()
}

async function alHacerClick(info) {
  const cita = info.event.extendedProps
  const esAsesorLiderAsignado = user.value?.id === cita.asesorLiderId
  const esCreador = user.value?.id === cita.creadoPor
  const puedeConfirmarCompletar = hasRole("admin") || esAsesorLiderAsignado
  const puedeCancelar = hasRole("admin") || hasRole("asesor_chat") || esAsesorLiderAsignado || esCreador
  const enEstadoFinal = ["completada", "cancelada"].includes(cita.estado)
  // Cualquier persona con acceso al módulo puede editar los datos de la cita
  // mientras no esté en un estado final; el historial registra quién hizo qué cambio.
  const puedeEditar = !enEstadoFinal

  const botones = []
  if (!enEstadoFinal && puedeConfirmarCompletar && cita.estado === "pendiente") botones.push("confirmada")
  if (!enEstadoFinal && puedeConfirmarCompletar && cita.estado === "confirmada") botones.push("completada")
  if (!enEstadoFinal && puedeCancelar) botones.push("cancelada")

  const html = `
    <div style="text-align:left">
      <p><b>Fecha:</b> ${formatFecha(cita.fecha)}</p>
      <p><b>Hora:</b> ${cita.horaInicio} - ${cita.horaFin}</p>
      <p><b>Cliente:</b> ${cita.nombreCliente}</p>
      <p><b>Celular:</b> ${cita.celular}</p>
      <p><b>Interesado en:</b> ${cita.interesadoEn || "—"}</p>
      <p><b>Asesor líder:</b> ${cita.asesorLiderNombre}</p>
      <p><b>Ciudad:</b> ${cita.ciudadNombre || "—"}</p>
      <p><b>Tipo:</b> ${cita.tipo === "virtual" ? "Virtual" : "Presencial"}</p>
      ${cita.tipo === "virtual" && cita.linkReunion ? `<p><b>Link de Meet:</b> <a href="${cita.linkReunion}" target="_blank" rel="noopener">${cita.linkReunion}</a></p>` : ""}
      <p><b>Estado:</b> ${LABELS_ESTADO[cita.estado]}</p>
      <p><b>Creado por:</b> ${cita.creadoPorNombre || "—"}</p>
      <p><a href="#" id="link-ver-historial">Ver historial de cambios</a></p>
    </div>
  `

  let quiereVerHistorial = false
  const didOpen = (popup) => {
    popup.querySelector("#link-ver-historial")?.addEventListener("click", (e) => {
      e.preventDefault()
      quiereVerHistorial = true
      Swal.close()
    })
  }

  if (botones.length === 0 && !puedeEditar) {
    await Swal.fire({ title: "Detalle de la cita", html, icon: "info", didOpen })
    if (quiereVerHistorial) citaEnHistorial.value = cita
    return
  }

  if (botones.length === 0) {
    const { isConfirmed } = await Swal.fire({
      title: "Detalle de la cita",
      html,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Editar",
      cancelButtonText: "Cerrar",
      didOpen
    })
    if (quiereVerHistorial) {
      citaEnHistorial.value = cita
      return
    }
    if (isConfirmed) citaEnEdicion.value = cita
    return
  }

  const acciones = { confirmada: "Confirmar", completada: "Completar", cancelada: "Cancelar" }
  const { value: accion, isDenied } = await Swal.fire({
    title: "Detalle de la cita",
    html,
    icon: "info",
    input: "radio",
    inputOptions: Object.fromEntries(botones.map((b) => [b, acciones[b]])),
    showCancelButton: true,
    showDenyButton: puedeEditar,
    denyButtonText: "Editar",
    confirmButtonText: "Aplicar",
    cancelButtonText: "Cerrar",
    didOpen
  })

  if (quiereVerHistorial) {
    citaEnHistorial.value = cita
    return
  }

  if (isDenied) {
    citaEnEdicion.value = cita
    return
  }

  if (accion) await cambiarEstado(cita, accion)
}

function renderizarEvento(info) {
  const cita = info.event.extendedProps
  const icono = document.createElement("i")
  icono.className = `bi ${cita.tipo === "virtual" ? "bi-camera-video" : "bi-geo-alt"} cita-evento-icono`

  const texto = document.createElement("span")
  texto.className = "cita-evento-texto"
  texto.textContent = info.event.title
  texto.title = info.event.title

  const wrapper = document.createElement("div")
  wrapper.className = "cita-evento-contenido"
  wrapper.append(icono, texto)

  return { domNodes: [wrapper] }
}

const calendarOptions = {
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  locale: esLocale,
  height: "auto",
  headerToolbar: {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay"
  },
  events: cargarEventos,
  eventClick: alHacerClick,
  eventContent: renderizarEvento
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-end flex-wrap gap-4">
        <h5 class="mb-0"><i class="ni ni-calendar-grid-58 text-primary me-2"></i>Calendario de Citas</h5>
        <div class="d-flex align-items-end gap-4 flex-wrap">
          <div class="filtro-asesor">
            <label class="form-label text-sm mb-1">Ver citas de</label>
            <select class="form-select form-select-sm filtro-asesor-select" v-model="filtroAsesor" @change="alCambiarFiltro">
              <option value="">Todas las citas</option>
              <option v-if="!esPrivilegiado" value="propias">Mis citas</option>
              <template v-else>
                <option v-for="a in asesoresLideres" :key="a.id" :value="String(a.id)" :title="a.name">{{ a.name }}</option>
              </template>
            </select>
          </div>
          <div class="d-flex gap-3 flex-wrap leyenda-estados">
            <span v-for="(color, estado) in COLORES_ESTADO" :key="estado" class="d-flex align-items-center gap-1 text-sm">
              <span class="legend-dot" :style="{ background: color }"></span>{{ LABELS_ESTADO[estado] }}
            </span>
          </div>
          <router-link
            :to="{ name: 'CitasMisCitas', query: { archivada: 'true' } }"
            class="btn btn-sm btn-outline-secondary mb-0"
            title="Ver citas completadas, canceladas o con fecha ya vencida"
          >
            <i class="bi bi-archive me-1"></i>Ver archivadas
          </router-link>
        </div>
      </div>
      <div class="card-body">
        <FullCalendar ref="recargarRef" :options="calendarOptions" />
      </div>
    </div>

    <EditarCitaModal
      :visible="!!citaEnEdicion"
      :cita="citaEnEdicion"
      @close="cerrarEdicion"
      @guardado="alGuardarEdicion"
    />

    <HistorialCitaModal
      :visible="!!citaEnHistorial"
      :cita-id="citaEnHistorial?.id"
      @close="cerrarHistorial"
    />
  </div>
</template>

<style scoped>
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.leyenda-estados {
  row-gap: 0.5rem;
}

/* Ancho fijo para que el desplegable no cambie de tamaño (y no "desface" el
   resto del header) al alternar entre "Todas las citas" y un nombre largo. */
.filtro-asesor {
  min-width: 0;
}

.filtro-asesor-select {
  width: 220px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* El contenido de cada evento es un nodo custom (renderizarEvento); sin estas
   reglas el texto largo se sale de la casilla del calendario en vez de
   truncarse dentro de ella. */
:deep(.cita-evento-contenido) {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

:deep(.cita-evento-icono) {
  flex-shrink: 0;
}

:deep(.cita-evento-texto) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

:deep(.fc-daygrid-event),
:deep(.fc-timegrid-event) {
  overflow: hidden;
}
</style>
